import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeAppleTicket } from '../../api/user/user.api';
import {
  consumeAuthReturnTo,
  getAccountBlockedInfo,
  getAuthReturnTo,
  redirectToAppIfNeeded,
  useAuth,
} from '@repo/common';

/**
 * Apple 로그인 콜백.
 *
 * 구글과 달리 Apple 은 인증 결과를 서버로 POST(form_post) 한다.
 * 서버가 검증·가입까지 끝낸 뒤 1회용 티켓만 쿼리로 붙여 이 페이지로 보내주며,
 * 여기서는 티켓을 토큰으로 교환하고 신규 가입이면 추가정보 화면으로 보낸다.
 */
const AppleCallbackPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('appleTicket');

    if (!ticket) {
      navigate('/login', { replace: true });
      return;
    }

    const handleAppleFlow = async () => {
      try {
        const res = await exchangeAppleTicket(ticket);
        const { newUser, user } = res.data ?? {};

        if (user?.accessToken) {
          localStorage.setItem('accessToken', user.accessToken);
        }

        // 앱에서 넘어온 로그인이면 추가정보 화면 대신 앱으로 돌려보낸다.
        if (await redirectToAppIfNeeded()) return;

        if (newUser) {
          const returnTo = getAuthReturnTo('/');
          const isCreator =
            sessionStorage.getItem('auth:isCreator') === 'true';

          navigate('/register/additional-info', {
            replace: true,
            state: {
              socialUserData: {
                socialEmail: user?.socialEmail,
                firstName: user?.firstName,
                lastName: user?.lastName,
                profileUrl: user?.profileUrl,
              },
              isCreator,
              returnTo,
            },
          });
          return;
        }

        await refreshUser();
        navigate(consumeAuthReturnTo('/'), { replace: true });
      } catch (error) {
        console.error('Apple Social Flow Error:', error);
        // 차단된 계정이면 로그인 화면에서 사유를 보여준다
        const blocked = getAccountBlockedInfo(error);
        navigate('/login', {
          replace: true,
          state: blocked ? { blocked: true, blockedReason: blocked.reason } : undefined,
        });
      }
    };

    handleAppleFlow();
  }, [navigate, refreshUser]);

  return (
    <div className='flex items-center justify-center h-screen'>
      <p className='typo-b16'>Processing Apple Login...</p>
    </div>
  );
};

export default AppleCallbackPage;
